// Mock fixtures for every user-mode/state combo described in the product
// doc, so each screen can be tested without the real backend only ever
// returning individual/free/owner.
//
// Usage: see accountApi.js - setMockPersona("corporate_paid_member") (or
// ?mockPersona=corporate_paid_member in the URL) routes every accountApi
// call through these fixtures instead of hitting the real API.

const ORG_ID = "org_456";
const ORG_NAME = "Refine Finance";
const WORKSPACES = ["US Payroll", "India Research"];

const invoices = (withData = true) => ({
    items: withData
        ? [
            { id: "inv_1", dueDate: "2025-09-02", description: "Monthly Plan", amount: 20, currency: "USD", downloadUrl: "https://cdn.refine.finance/invoices/inv_1.pdf" },
            { id: "inv_2", dueDate: "2025-08-02", description: "Monthly Plan", amount: 20, currency: "USD", downloadUrl: "https://cdn.refine.finance/invoices/inv_2.pdf" },
            { id: "inv_3", dueDate: "2025-07-02", description: "Monthly Plan", amount: 20, currency: "USD", downloadUrl: "https://cdn.refine.finance/invoices/inv_3.pdf" },
            { id: "inv_4", dueDate: "2025-06-25", description: "Credit Top Up: 5000", amount: 273, currency: "USD", downloadUrl: "https://cdn.refine.finance/invoices/inv_4.pdf" },
        ]
        : [],
});

const modules = (tier) =>
    tier === "free"
        ? {
            hasAccess: [
                { id: "mod_1", name: "Screener", description: "Discover companies that match your chosen criteria", status: "open" },
                { id: "mod_2", name: "Analytics", description: "Fundamental financial analysis for global companies", status: "open" },

            ],
            noAccess: [
                { id: "mod_3", name: "Risk Map", description: "Objective risk evaluations for companies you track", status: "upgrade" },
                { id: "mod_4", name: "Watchlist", description: "Set alerts to stay updated on companies you follow", status: "upgrade" },
                { id: "mod_5", name: "Benchmarking", description: "Compare companies against each other on key metrics", status: "upgrade" },
            ],
        }
        : {
            hasAccess: [
                { id: "mod_1", name: "Screener", description: "Discover companies that match your chosen criteria", status: "open" },
                { id: "mod_2", name: "Analytics", description: "Fundamental financial analysis for global companies", status: "open" },
                { id: "mod_5", name: "Benchmarking", description: "Compare companies against each other on key metrics", status: "open" },
            ],
            noAccess: [
                { id: "mod_3", name: "Risk Map", description: "Objective risk evaluations for companies you track", status: "upgrade" },
                { id: "mod_4", name: "Watchlist", description: "Set alerts to stay updated on companies you follow", status: "upgrade" },
            ],
        };

const corporateWorkspaces = (tier, role) => ({
    free: {
        total: 2,
        items: [
            { id: "ws_1", name: "Investor Relations", membersCount: 1, description: "Investor Relations enabled", yourRole: "Member", hasAccess: true },
            { id: "ws_2", name: "M & A Analysis", membersCount: 1, description: "M&A Analysis enabled", yourRole: "Member", hasAccess: true },
        ],
    },
    paid:
        tier === "free"
            ? { total: 0, youHaveAccess: 0, youDontHaveAccess: 0, items: [] }
            : {
                total: 2,
                youHaveAccess: role === "member" ? 1 : 2,
                youDontHaveAccess: role === "member" ? 1 : 0,
                items: [
                    { id: "ws_3", name: "US Payroll", membersCount: 2, description: "Payroll automation with 1 AI agent", yourRole: role === "member" ? "Member" : role === "admin" ? "Admin" : "Owner", hasAccess: true },
                    { id: "ws_4", name: "India Research Desk", membersCount: 1, description: "Research & analytics", yourRole: role === "member" ? null : role === "admin" ? "Admin" : "Owner", hasAccess: role !== "member" },
                ],
            },
});

const teamMembers = (isYouId) => ({
    membersCount: 3,
    member_limit: 5,
    workspaces: WORKSPACES,
    members: [
        {
            id: "mem_owner", userId: "user_owner", name: "Rahul Sood", email: "rahul@refine.finance",
            avatar: null, isYou: isYouId === "mem_owner", aiCreditLimit: 800, status: "active",
            roles: { "US Payroll": "Owner", "India Research": "Owner" },
        },
        {
            id: "mem_admin", userId: "user_admin", name: "Priya Nair", email: "priya@refine.finance",
            avatar: null, isYou: isYouId === "mem_admin", aiCreditLimit: 500, status: "active",
            roles: { "US Payroll": "Admin", "India Research": "Admin" },
        },
        {
            id: "mem_member", userId: "user_member", name: "Sumit Uniyal", email: "sumit.uniyal@refine.finance",
            avatar: null, isYou: isYouId === "mem_member", aiCreditLimit: 200, status: "pending",
            roles: { "US Payroll": "No Access", "India Research": "Member" },
        },
    ],
});

const notifications = (role) => ({
    unreadCount: role === "member" ? 0 : 1,
    items:
        role === "member"
            ? [
                {
                    id: "notif_2", type: "ORG_INVITE", title: "Organization Invite",
                    message: "You have been invited to join Refine Finance", isRead: true, isActionable: false,
                    createdAt: "2026-08-20T15:30:00Z", payload: { inviteId: "inv_111", organizationName: ORG_NAME },
                },
            ]
            : [
                {
                    id: "notif_1", type: "ACCESS_REQUEST",
                    title: `Sumit Uniyal requested access`,
                    message: `Sumit Uniyal requested access to become "Admin" of "US Payroll" Workspace`,
                    isRead: false, isActionable: true, createdAt: "2026-08-24T10:00:00Z",
                    payload: {
                        requestId: "req_789", workspaceId: "ws_3", workspaceName: "US Payroll",
                        requestedRole: "Admin", requesterName: "Sumit Uniyal", requesterEmail: "sumit.uniyal@refine.finance",
                    },
                },
                // {
                //     id: "notif_3", type: "ORG_INVITE", title: "Welcome to Refine Finance",
                //     message: "Your organization workspace has been set up successfully.",
                //     isRead: true, isActionable: false, createdAt: "2026-08-18T09:00:00Z",
                //     payload: { organizationName: ORG_NAME },
                // },
            ],
});

const brokerageApiKey = { masked: "rf_live_••••••••••••8f2c", baseUrl: "https://api.refine.finance/v1" };

export const MOCK_PERSONAS = {
    individual_free: {
        label: "Individual - Free",
        user: { id: "user_ind_free", name: "Aisha Khan", email: "aisha@example.com", avatar: null, userType: "individual", role: "owner", tier: "free" },
        plan: { userType: "individual", tier: "free", tierName: "Free Individual", modules: { free: 1, paid: 0 } },
        modules: modules("free"),
        workspaces: null,
        members: null,
        notifications: { unreadCount: 0, items: [] },
        invoices: invoices(false),
    },

    individual_paid: {
        label: "Individual - Paid",
        user: { id: "user_ind_paid", name: "Aisha Khan", email: "aisha@example.com", avatar: null, userType: "individual", role: "owner", tier: "paid" },
        plan: { userType: "individual", tier: "paid", tierName: "Paid Individual", modules: { free: 1, paid: 3 } },
        modules: modules("paid"),
        workspaces: null,
        members: null,
        notifications: { unreadCount: 0, items: [] },
        invoices: invoices(true),
    },

    corporate_free_owner: {
        label: "Corporate - Free - Owner",
        user: { id: "user_owner", name: "Rahul Sood", email: "rahul@refine.finance", avatar: null, userType: "org_corporate", role: "owner", tier: "free", organization: { id: ORG_ID, name: ORG_NAME } },
        plan: { userType: "org_corporate", tier: "free", tierName: "Free Starter", organizationName: ORG_NAME, workspaces: { free: 2, paid: 0 }, members: { current: 3, limit: 3 }, orgAiCredits: { used: 0, total: 1000 }, myAiCredits: { used: 0, total: 200 } },
        modules: null,
        workspaces: corporateWorkspaces("free", "owner"),
        members: teamMembers("mem_owner"),
        notifications: notifications("owner"),
        invoices: invoices(true),
    },

    corporate_free_admin: {
        label: "Corporate - Free - Admin",
        user: { id: "user_admin", name: "Priya Nair", email: "priya@refine.finance", avatar: null, userType: "org_corporate", role: "admin", tier: "free", organization: { id: ORG_ID, name: ORG_NAME } },
        plan: { userType: "org_corporate", tier: "free", tierName: "Free Starter", organizationName: ORG_NAME, workspaces: { free: 2, paid: 0 }, members: { current: 3, limit: 3 }, orgAiCredits: { used: 0, total: 1000 }, myAiCredits: { used: 0, total: 150 } },
        modules: null,
        workspaces: corporateWorkspaces("free", "admin"),
        members: teamMembers("mem_admin"),
        notifications: notifications("admin"),
        invoices: invoices(true),
    },

    corporate_free_member: {
        label: "Corporate - Free - Member",
        user: { id: "user_member", name: "Sumit Uniyal", email: "sumit.uniyal@refine.finance", avatar: null, userType: "org_corporate", role: "member", tier: "free", organization: { id: ORG_ID, name: ORG_NAME } },
        plan: { userType: "org_corporate", tier: "free", tierName: "Free Starter", organizationName: ORG_NAME, workspaces: { free: 2, paid: 0 }, members: { current: 3, limit: 3 }, orgAiCredits: { used: 0, total: 1000 }, myAiCredits: { used: 0, total: 200 } },
        modules: null,
        workspaces: corporateWorkspaces("free", "member"),
        members: teamMembers("mem_member"),
        notifications: notifications("member"),
        invoices: invoices(true),
    },

    corporate_paid_owner: {
        label: "Corporate - Paid - Owner",
        user: { id: "user_owner", name: "Rahul Sood", email: "rahul@refine.finance", avatar: null, userType: "org_corporate", role: "owner", tier: "paid", organization: { id: ORG_ID, name: ORG_NAME } },
        plan: { userType: "org_corporate", tier: "paid", tierName: "Paid Organization - Corporate", organizationName: ORG_NAME, workspaces: { free: 2, paid: 2 }, members: { current: 3, limit: 5 }, orgAiCredits: { used: 1200, total: 5000 }, myAiCredits: { used: 300, total: 1000 } },
        modules: null,
        workspaces: corporateWorkspaces("paid", "owner"),
        members: teamMembers("mem_owner"),
        notifications: notifications("owner"),
        invoices: invoices(true),
    },

    corporate_paid_admin: {
        label: "Corporate - Paid - Admin",
        user: { id: "user_admin", name: "Priya Nair", email: "priya@refine.finance", avatar: null, userType: "org_corporate", role: "admin", tier: "paid", organization: { id: ORG_ID, name: ORG_NAME } },
        plan: { userType: "org_corporate", tier: "paid", tierName: "Paid Organization - Corporate", organizationName: ORG_NAME, workspaces: { free: 2, paid: 2 }, members: { current: 3, limit: 5 }, orgAiCredits: { used: 1200, total: 5000 }, myAiCredits: { used: 500, total: 1000 } },
        modules: null,
        workspaces: corporateWorkspaces("paid", "admin"),
        members: teamMembers("mem_admin"),
        notifications: notifications("admin"),
        invoices: invoices(true),
    },

    corporate_paid_member: {
        label: "Corporate - Paid - Member",
        user: { id: "user_member", name: "Sumit Uniyal", email: "sumit.uniyal@refine.finance", avatar: null, userType: "org_corporate", role: "member", tier: "paid", organization: { id: ORG_ID, name: ORG_NAME } },
        plan: { userType: "org_corporate", tier: "paid", tierName: "Paid Organization - Corporate", organizationName: ORG_NAME, workspaces: { free: 2, paid: 2 }, members: { current: 3, limit: 5 }, orgAiCredits: { used: 1200, total: 5000 }, myAiCredits: { used: 200, total: 1000 } },
        modules: null,
        workspaces: corporateWorkspaces("paid", "member"),
        members: teamMembers("mem_member"),
        notifications: notifications("member"),
        invoices: invoices(true),
    },

    brokerage_free: {
        label: "Brokerage - Free",
        user: { id: "user_brk_free", name: "Vikram Malhotra", email: "vikram@refine.finance", avatar: null, userType: "org_brokerage", role: "owner", tier: "free", organization: { id: ORG_ID, name: ORG_NAME } },
        plan: {
            userType: "org_brokerage", tier: "free", tierName: "Free Organization - Brokerage", organizationName: ORG_NAME,
            usage: { used: 8200, total: 10000 },
            limits: { rateLimit: "20 req/min", dataHistory: "1 year", dataFrequency: "Monthly", dataFormat: "CSV" },
            apiKey: brokerageApiKey,
        },
        modules: null,
        workspaces: null,
        members: null,
        notifications: { unreadCount: 0, items: [] },
        invoices: invoices(false),
    },

    brokerage_paid: {
        label: "Brokerage - Paid",
        user: { id: "user_brk_paid", name: "Vikram Malhotra", email: "vikram@refine.finance", avatar: null, userType: "org_brokerage", role: "owner", tier: "paid", organization: { id: ORG_ID, name: ORG_NAME } },
        plan: {
            userType: "org_brokerage", tier: "paid", tierName: "Paid Organization - Brokerage", organizationName: ORG_NAME,
            usage: { used: 312400, total: 500000 },
            limits: { rateLimit: "100 req/min", dataHistory: "10 years", dataFrequency: "Yearly", dataFormat: "Raw" },
            apiKey: brokerageApiKey,
        },
        modules: null,
        workspaces: null,
        members: null,
        notifications: { unreadCount: 0, items: [] },
        invoices: invoices(true),
    },
};

export const MOCK_PERSONA_IDS = Object.keys(MOCK_PERSONAS);